<script lang="ts" setup>
import { Paragraph } from "types/Paragraph";
import {
  RevisionAnnotation,
  RevisionAnnotations,
} from "types/RevisionAnnotation";

const props = defineProps({
  paragraph: {
    type: Object as PropType<Paragraph>,
    required: true,
  },
  enableComments: {
    type: Boolean,
    default: true,
  },
});

const documentId = inject("documentId") as any;
const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const documentParagraphStore = useDocumentParagraphStore(
  documentId,
  props.paragraph.id
);
const documentParagraphsStore = useDocumentParagraphsStore(documentId);
const documentEvaluativeParagraphsStore =
  useDocumentEvaluativeParagraphsStore(documentId);
const { getEvaluativeParagraphByTokenIndex } = storeToRefs(
  documentEvaluativeParagraphsStore
);

const { paragraph: streamedParagraph } = storeToRefs(documentParagraphStore);

onMounted(() => {
  documentParagraphStore.streamParagraph(props.paragraph);
});

const paragraph = computed(() => {
  return streamedParagraph.value ?? props.paragraph;
});

const isList = computed(() => {
  return paragraph.value.formatting.isList == true;
});

const leftMargin = computed(() => {
  if (isList.value != true) {
    return paragraph.value.formatting.leftIndent + "pt";
  }

  if (
    !paragraph.value.formatting.listItemInfo ||
    !paragraph.value.formatting.listItemInfo.indentStart ||
    !paragraph.value.formatting.listItemInfo.indentStart.magnitude ||
    !paragraph.value.formatting.listItemInfo.indentStart.unit
  )
    return 0;

  return `${paragraph.value.formatting.listItemInfo.indentStart.magnitude}${paragraph.value.formatting.listItemInfo.indentStart.unit}`;
});

const textAlignment = (alignment: string) => {
  switch (alignment) {
    case "RIGHT":
      return "text-right";
    case "CENTER":
      return "text-center";
    case "JUSTIFIED":
      return "text-justify";
    case "LEFT":
    default:
      return "text-left";
  }
};

const paragraphClass = computed(() => {
  const pClass: { [key: string]: any } = {};
  const paragraphAlignment = textAlignment(
    paragraph.value.formatting.alignment
  );
  pClass[`${paragraphAlignment}`] = true;

  // pClass["mt-4"] = paragraph.section == "header";

  return pClass;
});

const { indentationIncrement } = useTokenFormatter();

const documentAnnotaitonStore = useDocumentAnnotationsStore(documentId);
const { allAnnotations } = storeToRefs(documentAnnotaitonStore);

const preTokenInlineAnnotations = computed(() => {
  return (token: ParagraphToken): RevisionAnnotations => {
    return useStartLineAnnotations(
      allAnnotations.value,
      paragraph.value,
      token
    );
  };
});

const postTokenInlineAnnotations = computed(() => {
  return (token: ParagraphToken): RevisionAnnotations => {
    const tokenIndex = paragraph.value.tokens.indexOf(token);

    return allAnnotations.value.filter((annotation: RevisionAnnotation) => {
      annotation.tokenGlobalPositions.sort((a, b) => a - b);

      // IEW decided it should always come after
      // We also want any annotations where isEndLine is true and it is attached to this paragraph
      // And this is token is the last token in the paragraph
      // New condition: if the annotation has forceInline off then the annotation comment can't be considered long, it should bump to the next lne
      if (
        annotation.isEndLine == true &&
        annotation.paragraphId == paragraph.value.id &&
        (annotation.forceInline == true ||
          useIsAnnotationCommentLong(annotation) != true)
      ) {
        // this should make things backwards compatible with the old way of doing end line
        // I have now introduced the abiltiy to break a paragraph into token groups if there are unexpected line break we need to use to visually render multiple paragraphs

        return annotation.lastTokenGlobalPosition
          ? annotation.lastTokenGlobalPosition == token.globalPosition
          : tokenIndex ==
              useParagraphTokensWithoutLinebreaks(paragraph.value).length - 1;
      }

      return (
        annotation.tokenGlobalPositions[
          annotation.tokenGlobalPositions.length - 1
        ] == token.globalPosition
      );
    });
  };
});

const userDocumentSettingsStore = useUserDocumentSettings();
const { allowInlineAnnotations } = storeToRefs(userDocumentSettingsStore);

const documentShowFormattingStore = useDocumentShowFormattingStore(documentId);
const { shouldShowFormatting } = storeToRefs(documentShowFormattingStore);

const docusmentSelectionStore = useDocumentSelectionStore(documentId);

const { setAnnotationBeingEdited, createDefaultAnnotation } =
  useAnnotationFormState();

const addEndOfLineAnnotation = (tokens: ParagraphTokens) => {
  if (props.enableComments != true) return;

  docusmentSelectionStore.clearSelection();

  const annotation = createDefaultAnnotation();
  annotation.forceInline = true;
  annotation.forceAfterToken = true;
  annotation.isEndLine = true;
  annotation.lastTokenGlobalPosition = tokens[tokens.length - 1].globalPosition;
  annotation.paragraphId = paragraph.value.id;

  setAnnotationBeingEdited(documentId, annotation);
};

const addInlineEditingEndOfLineAnnotation = (tokens: ParagraphTokens) => {
  if (props.enableComments != true) return;

  docusmentSelectionStore.clearSelection();
  const lastTokenGlobalPosition = tokens[tokens.length - 1].globalPosition;
  useCreateInlineEditingAnnotation(
    documentId,
    paragraph.value.id,
    true,
    lastTokenGlobalPosition
  );
};

const addStartOfLineAnnotation = (tokens: ParagraphTokens) => {
  if (props.enableComments != true) return;

  docusmentSelectionStore.clearSelection();

  const annotation = createDefaultAnnotation();
  annotation.forceInline = true;
  annotation.forceAfterToken = true;
  annotation.isStartLine = true;
  annotation.lastTokenGlobalPosition = tokens[0].globalPosition;
  annotation.paragraphId = paragraph.value.id;

  setAnnotationBeingEdited(documentId, annotation);
};

const addInlineEditingStartOfLineAnnotation = (tokens: ParagraphTokens) => {
  if (props.enableComments != true) return;

  docusmentSelectionStore.clearSelection();
  const lastTokenGlobalPosition = tokens[0].globalPosition;
  useCreateInlineEditingAnnotation(
    documentId,
    paragraph.value.id,
    false,
    lastTokenGlobalPosition,
    true
  );
};

const deleteParagraph = () => {
  props.paragraph.isDeleted = true;

  if (streamedParagraph.value) {
    streamedParagraph.value.isDeleted = true;
    documentParagraphsStore.saveParagraph(streamedParagraph.value);
  }

  useDocumentChecklistStore(documentId).reinferAllCriteria();
};

const {
  isMergingParagraphs,
  isParagraphToBeMerged,
  isFixingAssignment,
  isParagraphSetToInsert,
} = storeToRefs(documentParagraphsStore);

const toggleParagraphToMerge = () => {
  documentParagraphsStore.toggleParagraphToMerge(paragraph.value);
};

const setParagraphToInsert = () => {
  documentParagraphsStore.setParagraphToInsert(paragraph.value);
};

const annotationFormStateStore = useAnnotationFormState();
const { isAnAnnotationBeingInlineEdited, isAnnotationBeingInlineEdited } =
  storeToRefs(annotationFormStateStore);

const documentSelectionStore = useDocumentSelectionStore(documentId);
const { hasSelection } = storeToRefs(documentSelectionStore);

const documentChecklistStore = useDocumentChecklistStore(documentId);
const { isOSS } = storeToRefs(documentChecklistStore);

const isIgnored = computed(() => {
  const tokenIndex = streamedParagraph.value?.tokens[0]?.position;

  if (tokenIndex == undefined) return false;

  const evaluativeParagraph =
    getEvaluativeParagraphByTokenIndex.value(tokenIndex);

  if (evaluativeParagraph == undefined) return false;

  return evaluativeParagraph.section == EvaluativeParagraphSection.ignore;
});

const isParagraphEmpty = computed(() => {
  return (
    streamedParagraph.value?.tokens.length == 0 ||
    streamedParagraph.value?.text.trim() == ""
  );
});

const spaceBelow = computed(() => {
  if (paragraph.value.formatting.spaceBelow == undefined) {
    return undefined;
  }

  return paragraph.value.formatting.spaceBelow + "px";
});

const spaceAbove = computed(() => {
  if (paragraph.value.formatting.spaceAbove == undefined) {
    return undefined;
  }

  return paragraph.value.formatting.spaceAbove + "px";
});

const lineSpanHeight = computed(() => {
  return paragraph.value.formatting.lineSpacing ?? 1;
});

const lineSpacingLabel = computed(() => {
  let lineSpacing: number | string = paragraph.value.formatting.lineSpacing;

  if (lineSpacing == undefined) {
    return "";
  }

  switch (paragraph.value.formatting.lineSpacing) {
    case 1:
      lineSpacing = "1";
      break;
    case 1.5:
      lineSpacing = "1.5";
      break;
    case 2:
      lineSpacing = "2";
      break;
    default:
      lineSpacing = lineSpacing.toFixed(2);
      break;
  }

  return `x${lineSpacing}`;
});
</script>

<template>
  <span
    v-if="streamedParagraph && paragraph.isDeleted != true"
    class="selection:bg-none"
  >
    <h2
      v-if="
        isOSS &&
        paragraph.collectionRef == 'paragraphs' &&
        paragraph.localIndex == 0
      "
      class="block text-h3 my-2 mb-4 font-bold"
      :style="{
        'font-family': 'Times New Roman',
        'font-size': '18pt',
      }"
    >
      Writing Assignment
    </h2>
    <div
      :data-paragraph-id="paragraph.id"
      :data-paragraph-collection="paragraph.collectionRef"
      class="cursor-text relative break-words"
      :class="{
        ...paragraphClass,
        ' bg-danger-default bg-opacity-10 rounded px-2': isIgnored,
      }"
      :style="{
        'margin-left': leftMargin,
        'text-indent':
          shouldShowFormatting != true
            ? paragraph.formatting.firstLineIndent + 'pt'
            : '',
        'margin-bottom': spaceBelow,
        'margin-top': spaceAbove,
      }"
    >
      <BaseIconButton
        v-if="isIgnored == true"
        v-tooltip="'You indicated this paragraph should be ignored.'"
        class="!absolute top-[50%] left-[-39px] translate-y-[-50%] rounded flex flex-col p-0"
        :size="35"
      >
        <icon name="material-symbols:help" size="50" />
      </BaseIconButton>
      <DocumentParagraphAnnotations
        v-if="paragraph.localIndex == 0"
        :paragraph="paragraph"
        :token-group="paragraph.tokens"
        :is-before-paragraph="true"
      />
      <div
        v-if="isMergingParagraphs"
        class="absolute top-0 left-0 w-full h-full rounded hover:bg-dark hover:bg-opacity-10 cursor-pointer"
        :class="{
          ' bg-primary-default bg-opacity-10': isParagraphToBeMerged(paragraph),
        }"
        @click="toggleParagraphToMerge"
      >
        &nbsp;
      </div>
      <div
        v-if="isFixingAssignment"
        class="absolute top-0 left-0 w-full h-full rounded hover:bg-dark hover:bg-opacity-10 cursor-pointer"
        :class="{
          ' bg-primary-default bg-opacity-10':
            isParagraphSetToInsert(paragraph),
        }"
        @click="setParagraphToInsert"
      >
        &nbsp;
      </div>
      <span
        v-if="isList"
        class="mr-2"
        :class="{
          ' underline':
            paragraph.formatting.listItemInfo?.formatting?.underline,
          ' font-[600]': paragraph.formatting.listItemInfo?.formatting?.bold,
          italic: paragraph.formatting.listItemInfo?.formatting?.italic,
          ' line-through':
            paragraph.formatting.listItemInfo?.formatting?.strikeThrough,
        }"
        :style="{
          'font-family': paragraph.formatting.listItemInfo?.formatting?.font,
          'font-size': paragraph.formatting.listItemInfo?.formatting?.size
            ? paragraph.formatting.listItemInfo?.formatting?.size + 'pt'
            : '12pt',
          color: useArrayToRGB(
            paragraph.formatting.listItemInfo?.formatting?.color ?? []
          ),
        }"
      >
        {{ useCreateListItemString(paragraph.formatting.listItemInfo) }}
      </span>

      <BaseClickHandler
        v-if="
          enableComments &&
          isTeacherViewMode &&
          isParagraphEmpty != true &&
          enableComments &&
          isTeacherViewMode &&
          paragraph.tokens.length > 0
        "
        class="cursor-pointer select-none selection:bg-none mr-1 absolute left-[-12px] flex flex-col items-center justify-center"
        :style="{
          height: lineSpanHeight * 16 + 'px',
        }"
        @click="addStartOfLineAnnotation(paragraph.tokens)"
        @dblclick="addInlineEditingStartOfLineAnnotation(paragraph.tokens)"
      >
        <div
          class="w-[10px] h-[20px] hover:bg-opacity-10 hover:bg-primary-default"
        />
      </BaseClickHandler>

      <span
        v-if="
          paragraph.formatting.firstLineIndent > 0 &&
          shouldShowFormatting == true
        "
        class="text-danger-default text-[10px]"
        >→</span
      >

      <span
        v-for="token in streamedParagraph.tokens"
        :key="token.id"
        class="inline"
      >
        <DocumentInlineAnnotation
          v-for="annotation in preTokenInlineAnnotations(token)"
          :key="(annotation.id ?? '') + annotation.lastUpdatedTimestamp"
          :annotation="annotation"
        />
        <DocumentParagraphsToken
          :key="token.id"
          :base-token="token"
          :paragraph-formatting="streamedParagraph.formatting"
          :is-assignment-token="streamedParagraph.collectionRef == 'paragraphs'"
          :enable-comments="enableComments"
        />
        <DocumentInlineAnnotation
          v-for="annotation in postTokenInlineAnnotations(token)"
          :key="(annotation.id ?? '') + annotation.lastUpdatedTimestamp"
          :annotation="annotation"
          :use-lines="false"
        />
      </span>

      <span v-if="shouldShowFormatting" class="inline">
        <icon
          name="fa-solid:paragraph"
          size="12"
          class="ml-[2px] mb-[2px] text-danger-default"
        />
        <span class="text-danger-default text-[12px] ml-1">{{
          lineSpacingLabel
        }}</span>
      </span>
      <BaseClickHandler
        v-if="
          enableComments &&
          isTeacherViewMode &&
          isParagraphEmpty != true &&
          enableComments &&
          isTeacherViewMode &&
          paragraph.tokens.length > 0
        "
        class="hover:bg-opacity-10 hover:bg-primary-default cursor-pointer ml-1 select-none selection:bg-none"
        @click="addEndOfLineAnnotation(paragraph.tokens)"
        @dblclick="addInlineEditingEndOfLineAnnotation(paragraph.tokens)"
      >
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
      </BaseClickHandler>
      <DocumentParagraphAnnotations
        :paragraph="paragraph"
        :token-group="paragraph.tokens"
      />
    </div>
  </span>
</template>

<style scoped></style>
